import { useState, type ReactNode } from "react";
import type { QueryKey } from "@tanstack/react-query";
import { MIN_REASON_LENGTH, type ActionAvailability } from "@amber/contracts";
import { isApiError } from "../api";
import { Button, type ButtonSize } from "../Button";
import { Callout } from "../Callout";
import { useCommandMutation } from "../data/command";
import { errorText } from "../data/errors";
import { Dialog, DialogText } from "../Dialog";
import { ReasonField } from "../Field";
import { useToast } from "../Toast";

export const REASON_TOO_SHORT = `Enter a reason of at least ${String(MIN_REASON_LENGTH)} characters`;

export interface ReasonRule {
  readonly minLength: number;
  readonly message: string;
}

const MIN_REASON: ReasonRule = { minLength: MIN_REASON_LENGTH, message: REASON_TOO_SHORT };

export interface Reason {
  readonly reason: string;
  readonly setReason: (value: string) => void;
  readonly trimmed: string;
  readonly ok: boolean;
  readonly submitted: boolean;
  readonly error: string | null;
  readonly submit: () => boolean;
  readonly reset: () => void;
}

export function useReason(serverError?: string | null, rule: ReasonRule = MIN_REASON): Reason {
  const [reason, setReason] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const trimmed = reason.trim();
  const ok = trimmed.length >= rule.minLength;
  return {
    reason,
    setReason,
    trimmed,
    ok,
    submitted,
    error: serverError ?? (submitted && !ok ? rule.message : null),
    submit: () => {
      setSubmitted(true);
      return ok;
    },
    reset: () => {
      setReason("");
      setSubmitted(false);
    },
  };
}

export function DialogFooter({
  onClose,
  confirm,
  pending = false,
  failed = false,
  danger = false,
  disabled,
  disabledReason,
  cancel = "Cancel",
}: {
  onClose: () => void;
  confirm: ReactNode;
  pending?: boolean;
  failed?: boolean;
  danger?: boolean;
  disabled?: boolean;
  disabledReason?: string | null | undefined;
  cancel?: string;
}) {
  return (
    <>
      <Button onClick={onClose}>{cancel}</Button>
      <Button
        type="submit"
        variant={danger ? "dangerPrimary" : "primary"}
        disabled={disabled}
        disabledReason={disabledReason}
        aria-busy={pending || undefined}
      >
        {failed ? "Try again" : confirm}
      </Button>
    </>
  );
}

export function FailedCallout({
  error,
  fieldsShown = false,
  className = "mt-3",
  fallback,
}: {
  error: unknown;
  fieldsShown?: boolean;
  className?: string;
  fallback?: string;
}) {
  if (!error || (fieldsShown && isApiError(error) && error.issues.length > 0)) return null;
  return (
    <Callout tone="attention" role="alert" className={className}>
      {errorText(error, fallback)}
    </Callout>
  );
}

export interface ReasonDialogProps {
  title: ReactNode;
  confirm: string;
  success: string;
  onClose: () => void;
  run: (reason: string, csrf: string | null) => Promise<unknown>;
  invalidate: readonly QueryKey[];
  invalidateOnConflict?: readonly QueryKey[];
  onDone?: () => void;
  children?: ReactNode;
  extra?: ReactNode;
  after?: ReactNode;
  danger?: boolean;
  wide?: boolean;
  ready?: boolean;
  disabledReason?: string | null;
  placeholder?: string;
}

export function ReasonDialog({
  title,
  confirm,
  success,
  onClose,
  run,
  invalidate,
  invalidateOnConflict,
  onDone,
  children,
  extra,
  after,
  danger = false,
  wide = false,
  ready = true,
  disabledReason,
  placeholder,
}: ReasonDialogProps) {
  const toast = useToast();
  const mutation = useCommandMutation<unknown, string>({
    run: (csrf, text) => run(text, csrf),
    invalidate,
    ...(invalidateOnConflict ? { invalidateOnConflict } : {}),
    onSuccess: () => {
      toast.show(success);
      onDone?.();
      onClose();
    },
  });
  const reason = useReason(isApiError(mutation.error) ? mutation.error.fieldErrors()["reason"] : null);
  const submit = () => {
    if (reason.submit() && ready && !disabledReason) mutation.mutate(reason.trimmed);
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={title}
      wide={wide}
      onSubmit={submit}
      footer={
        <DialogFooter
          onClose={onClose}
          confirm={confirm}
          danger={danger}
          pending={mutation.isPending}
          failed={mutation.isError}
          disabledReason={disabledReason}
        />
      }
    >
      {children ? <DialogText>{children}</DialogText> : null}
      {extra}
      <ReasonField
        value={reason.reason}
        onChange={reason.setReason}
        error={reason.error}
        {...(placeholder ? { placeholder } : {})}
      />
      {after}
      <FailedCallout error={mutation.error} fieldsShown />
    </Dialog>
  );
}

export function ActionButton({
  action,
  label,
  onClick,
  danger = false,
  size = "xs",
}: {
  action: ActionAvailability | undefined;
  label: string;
  onClick: () => void;
  danger?: boolean;
  size?: ButtonSize;
}) {
  if (!action) return null;
  return (
    <Button
      size={size}
      variant={danger ? "danger" : "default"}
      disabledReason={action.enabled ? null : action.reason}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}
